import { Component, computed, inject, ChangeDetectionStrategy } from '@angular/core';
import { DatePipe, formatDate } from '@angular/common';
import { Router } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { AccordionModule } from '@openng/optimus-ui/accordion';
import { Tooltip } from '@openng/optimus-ui/tooltip';
import { SessionService } from '../../../../services/session.service';
import { GuidePageObject } from '@libs/types/src/services/object-type/guide-page.object';
import { formatPerson } from '@libs/shared/helpers/person.utils';
import { BaseDialogComponent } from '../../../../components/kit/base-dialog/base-dialog.component';
import { normalizeGuidePageUrl } from '../../../../utils/router.utils';
import { GuideStore } from './guide.store';
import { GuideHtmlPipe } from './guide-html.pipe';

@Component({
    selector: 'app-guide',
    templateUrl: './guide.component.html',
    imports: [TranslatePipe, AccordionModule, DatePipe, Tooltip, GuideHtmlPipe],
    changeDetection: ChangeDetectionStrategy.Eager,
    providers: [GuideStore],
})
export class GuideComponent extends BaseDialogComponent {

    protected readonly store = inject(GuideStore);
    private readonly session = inject(SessionService);
    private readonly translate = inject(TranslateService);

    private readonly router = inject(Router);
    private readonly routerEvents = toSignal(this.router.events);

    protected readonly pages = computed(() => {
        this.routerEvents();
        const url = normalizeGuidePageUrl(this.router.url);
        return this.store.guidePages()
            .filter((p) => p.active && p.url === url)
            .sort((a, b) => (b.lastChangeAt ?? '').localeCompare(a.lastChangeAt ?? ''));
    });

    private readonly registeredViews = new Set<number>();

    protected isOutdated(page: GuidePageObject): boolean {
        if (!page.lastView || !page.lastChangeAt) return false;
        return new Date(page.lastChangeAt) > new Date(page.lastView);
    }

    protected lastChangeTooltip(page: GuidePageObject): string {
        const changeDate = page.lastChangeAt
            ? formatDate(page.lastChangeAt, 'd.M.y H:mm', this.session.locale)
            : '';
        const authorName = formatPerson(page.lastChangeByUser);
        const author = authorName ? ` — ${authorName}` : '';

        const lastViewedAt = page.lastView;
        if (lastViewedAt && this.isOutdated(page)) {
            const readDate = formatDate(lastViewedAt, 'd.M.y H:mm', this.session.locale);
            const readLabel = this.translate.instant('layout.guide.tooltip.read');
            const updatedLabel = this.translate.instant('layout.guide.tooltip.updated');
            return `${readLabel} ${readDate}, ${updatedLabel} ${changeDate}${author}`;
        }

        return `${changeDate}${author}`;
    }

    protected async onPanelOpen(id: number): Promise<void> {
        const page = this.pages().find((p) => p.id === id);
        if (!page || this.registeredViews.has(page.id)) return;

        this.registeredViews.add(page.id);
        await this.store.api.registerGuidePageView({ guidePageId: page.id });
        await this.store.globalGlobalStore.guidePageStore.load();
    }

}
